import { useEffect } from 'react';

const MoverTable = ({ movers, onViewDetails, onEdit, onSuspend, onAddLeads, selectedMovers, onSelectMover, onSelectAll }) => {
  const allSelected = movers.length > 0 && selectedMovers.length === movers.length;
  const someSelected = selectedMovers.length > 0 && selectedMovers.length < movers.length;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.dropdown-menu') && !e.target.closest('button')) {
        document.querySelectorAll('.dropdown-menu').forEach(menu => {
          menu.classList.add('hidden');
        });
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-gray-100 text-gray-800',
      suspended: 'bg-red-100 text-red-800'
    };
    return styles[status] || styles.pending;
  };

  const getRemainingLeads = (mover) => {
    const totalLeads = Number(mover.total_leads || 0);
    const consumedLeads = Number(mover.consumed_leads || 0);

    if (totalLeads <= 0) return 0;

    return totalLeads - consumedLeads;
  };

  return (
    <div className="bg-white rounded-xl shadow-[0_6px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_10px_34px_rgba(0,0,0,0.10)] transition-all border border-gray-200">
      <div className="overflow-x-auto overflow-y-visible">
        <table className="w-full relative">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left">
                <input
                  type="checkbox"
                  className="rounded border-gray-300"
                  checked={allSelected}
                  ref={input => {
                    if (input) {
                      input.indeterminate = someSelected;
                    }
                  }}
                  onChange={onSelectAll}
                />
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Mover
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Rating
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Total Leads
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Consumed
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Remaining
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Revenue
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Insurance
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {movers.map((mover) => (
              <tr
                key={mover.id}
                className="hover:bg-gray-50 transition cursor-pointer group"
                onClick={() => onViewDetails(mover)}
              >
                <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="rounded border-gray-300"
                    checked={selectedMovers.includes(mover.id)}
                    onChange={() => onSelectMover(mover.id)}
                  />
                </td>
                <td className="px-6 py-4">
                  <div>
                    <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors uppercase">{mover.name}</div>
                    <div className="text-sm text-gray-500 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {mover.location}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(mover.status)}`}>
                    {mover.status}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1">
                    <span className="text-yellow-500">⭐</span>
                    <span className="text-sm font-medium text-gray-900">{mover.rating}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <span className="font-medium">{mover.total_leads || 0}</span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{mover.consumed_leads || 0}</td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {getRemainingLeads(mover) <= 0 ? (
                    <span className="font-medium text-red-600">No leads remaining</span>
                  ) : (
                    <span className="font-medium text-green-600">
                      {getRemainingLeads(mover)}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">${mover.revenue.toLocaleString()}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2 text-sm">
                    {mover.insuranceStatus === 'valid' ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {mover.insuranceExpiry}
                      </span>
                    ) : mover.insuranceStatus === 'pending' ? (
                      <span className="text-yellow-600 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Pending verification
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {mover.insuranceExpiry}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const button = e.currentTarget;
                        const rect = button.getBoundingClientRect();

                        // Close all other menus first
                        document.querySelectorAll('.dropdown-menu').forEach(menu => {
                          if (menu !== button.nextElementSibling) {
                            menu.classList.add('hidden');
                          }
                        });

                        const menu = button.nextElementSibling;
                        menu.classList.toggle('hidden');

                        // Position the menu using fixed positioning
                        if (!menu.classList.contains('hidden')) {
                          menu.style.position = 'fixed';
                          menu.style.top = `${rect.bottom + 4}px`;
                          menu.style.right = `${window.innerWidth - rect.right}px`;
                        }
                      }}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                      </svg>
                    </button>
                    <div className="dropdown-menu hidden w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-[9999]">
                      <button
                        onClick={() => onViewDetails(mover)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View Details
                      </button>
                      <button
                        onClick={() => onEdit(mover)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => onAddLeads(mover)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Leads
                      </button>
                      <button
                        onClick={() => {
                          document.querySelectorAll('.dropdown-menu').forEach(menu => {
                            menu.classList.add('hidden');
                          });
                          onSuspend(mover);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${mover.status === 'suspended' ? 'text-green-600' : 'text-gray-700'
                          }`}
                      >
                        {mover.status === 'suspended' ? (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Reactivate
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                            Suspend
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MoverTable;
